/**
 * Top-of-page brand header.
 *
 * Used on every logged-in page via SiteLayout. The brand mark is the
 * Hooten Young logo lockup (the same dagger-monogram + wordmark shown
 * on the landing-page hero), giving every screen a consistent left-
 * anchored brand identity. The right-hand cluster is "Home" + the
 * user menu, themed in the warm-cream / brand-gold palette so the
 * header reads as part of the same surface as the page below.
 *
 * The landing page renders its own hero, so it doesn't use this
 * component.
 */

import { Box, Tooltip, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import { NotesBell } from './NotesBell';
import { UserMenu } from './UserMenu';

const GOLD = '#bb8c3f';
const GOLD_LIGHT = '#e9c46a';
const IVORY = '#faf6ee';
const FONT_INTER = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';

interface HeaderProps {
  /** Optional context label rendered as a subtle eyebrow next to the
   *  logo (e.g. "Admin" on the admin pages). */
  subtitle?: string;
  /** Kept for backwards-compatibility with existing callers; ignored
   *  now that the logo itself is the home link (industry-standard
   *  pattern used by Stripe, Linear, GitHub, Vercel, etc.). */
  showHome?: boolean;
}

const MotionBox = motion.create(Box);

export function Header({ subtitle }: HeaderProps) {
  const location = useLocation();
  const isHome = location.pathname === '/';
  return (
    <MotionBox
      initial={{ y: -16, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.45, ease: 'easeOut' }}
      sx={{
        backgroundColor: IVORY,
        backdropFilter: 'blur(8px)',
        borderBottom: '1px solid rgba(187, 140, 63, 0.18)',
        position: 'sticky',
        top: 0,
        zIndex: 1100,
        // Hairline gold accent under the bottom border so every page
        // gets the same brand "wax seal" line as the auth pages.
        '&::after': {
          content: '""',
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: -1,
          height: '1px',
          background: `linear-gradient(to right, transparent 0%, ${GOLD} 25%, ${GOLD_LIGHT} 50%, ${GOLD} 75%, transparent 100%)`,
          opacity: 0.55,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 1.25, md: 1.5 },
          maxWidth: 1400,
          mx: 'auto',
        }}
      >
        {/* ── Left: brand lockup (clickable → home) ───────── */}
        <Tooltip
          title={isHome ? '' : 'Return to home'}
          arrow
          placement="bottom-start"
          enterDelay={500}
          slotProps={{
            tooltip: { sx: { fontFamily: FONT_INTER, fontSize: 11.5 } },
          }}
        >
          <RouterLink
            to="/"
            style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
            aria-label="Hooten Young — back to home"
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 1.5, sm: 2 },
                cursor: isHome ? 'default' : 'pointer',
                transition: 'opacity 180ms ease, transform 220ms ease',
                pointerEvents: isHome ? 'none' : 'auto',
                '&:hover .brand-logo': isHome
                  ? undefined
                  : { transform: 'scale(1.02)', opacity: 0.92 },
              }}
            >
              <Box
                className="brand-logo"
                component="img"
                src="/brand/hy-logo.png"
                alt="Hooten Young"
                sx={{
                  height: { xs: 36, sm: 44, md: 52 },
                  width: 'auto',
                  display: 'block',
                  transition: 'transform 220ms ease, opacity 180ms ease',
                  transformOrigin: 'left center',
                }}
              />
              {subtitle && (
                <>
                  <Box
                    aria-hidden
                    sx={{
                      width: '1px',
                      height: 26,
                      background: `linear-gradient(to bottom, transparent, ${GOLD}, transparent)`,
                      opacity: 0.55,
                      display: { xs: 'none', sm: 'block' },
                    }}
                  />
                  <Typography
                    sx={{
                      fontFamily: FONT_INTER,
                      fontSize: { xs: 10.5, sm: 11 },
                      fontWeight: 700,
                      color: GOLD,
                      letterSpacing: '0.24em',
                      textTransform: 'uppercase',
                      display: { xs: 'none', sm: 'inline' },
                    }}
                  >
                    {subtitle}
                  </Typography>
                </>
              )}
            </Box>
          </RouterLink>
        </Tooltip>

        {/* ── Right: unread-notes indicator + user menu ─────── */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <NotesBell />
          <UserMenu />
        </Box>
      </Box>
    </MotionBox>
  );
}
